/**
 * Firebase Analytics (GA4). No-ops when measurementId is missing or role is dev.
 * Never throws into product UI.
 */
import { initializeAnalytics, isSupported, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { firebaseApp } from '../auth/firebase';
import { shouldTrack, normalizePagePath, userPropertiesFor } from './appAnalyticsCore';

let identity = { uid: null, role: null, companyId: null, email: null };
let analytics = null;
let initTried = false;

function canSend() {
  return shouldTrack(identity.role, identity.email) && Boolean(identity.uid);
}

async function getClient() {
  if (!process.env.REACT_APP_FIREBASE_MEASUREMENT_ID) return null;
  if (initTried) return analytics;
  initTried = true;
  try {
    const ok = await isSupported();
    if (!ok) return null;
    // AnalyticsGate sends page_view on every route change; gtag's automatic one on init would double-count the first page.
    analytics = initializeAnalytics(firebaseApp, { config: { send_page_view: false } });
    return analytics;
  } catch (err) {
    console.warn('[analytics] init skipped', err);
    analytics = null;
    return null;
  }
}

export async function setAnalyticsIdentity({ uid, role, companyId, email }) {
  identity = { uid: uid || null, role: role || null, companyId: companyId || null, email: email || null };
  const client = await getClient();
  if (!client) return;
  try {
    if (canSend()) {
      setUserId(client, identity.uid);
      // User-scoped dimensions for GA4 (企業 × 役職 で切るため). Registered in GA4 Admin as
      // custom dimensions `company_id` / `role`, scope = User.
      const props = userPropertiesFor(identity);
      if (props) setUserProperties(client, props);
      const flag = `analytics_login_${identity.uid}`;
      if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(flag)) {
        sessionStorage.setItem(flag, '1');
        logEvent(client, 'login', { method: 'password' });
      }
    } else {
      setUserId(client, null);
    }
  } catch (err) {
    console.warn('[analytics] identity skipped', err);
  }
}

export async function trackEvent(name, params) {
  if (!canSend() || !name) return;
  const client = await getClient();
  if (!client) return;
  try {
    logEvent(client, name, params || {});
  } catch (err) {
    console.warn('[analytics] event skipped', name, err);
  }
}

export function trackPageView(pathname) {
  const pagePath = normalizePagePath(pathname);
  if (!pagePath) return;
  const pageLocation =
    typeof window !== 'undefined' ? `${window.location.origin}${pagePath}` : pagePath;
  return trackEvent('page_view', {
    page_path: pagePath,
    page_title: pagePath,
    page_location: pageLocation,
  });
}

export function trackTutorialComplete() {
  return trackEvent('tutorial_complete');
}

export function trackAiChatSend() {
  return trackEvent('ai_chat_send');
}

export function trackActionCheckin() {
  return trackEvent('action_checkin');
}

/**
 * 10%-step scroll depth (analyst request, replaces GTM's scroll trigger).
 * GA4 side: register event-scoped custom dimension `percent_scrolled`; turn OFF
 * enhanced measurement "scroll" so the built-in 90% event does not double up.
 */
export function trackScrollDepth(percent, pagePath) {
  return trackEvent('scroll_depth', { percent_scrolled: percent, page_path: pagePath });
}

/** 店舗 tab: この店舗 (`store`) or 全店比較 (`company`) opened — are the store views used at all? */
export function trackScopeChange(scope) {
  return trackEvent('scope_change', { scope });
}
